import { Modal } from '@/components/ui/modal';
import type { AutoMapCamerasResponse } from '@yawi/types';
import { Loader2, MapPin } from 'lucide-react';
import { useState } from 'react';

interface AutoMapResultModalProps {
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onAutoMap: (tenantId: string) => Promise<AutoMapCamerasResponse>;
}

type Phase = 'idle' | 'running' | 'done';

export function AutoMapResultModal({ tenantId, isOpen, onClose, onConfirm, onAutoMap }: AutoMapResultModalProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<AutoMapCamerasResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRun = async () => {
    setPhase('running');
    setError(null);
    try {
      const res = await onAutoMap(tenantId);
      setResult(res);
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al ejecutar el auto-mapeo');
      setPhase('idle');
    }
  };

  const handleClose = () => {
    if (phase === 'done') onConfirm();
    setPhase('idle');
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-2xl w-full">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <MapPin className="w-5 h-5 text-indigo-500" />
          <h2 className="text-lg font-semibold text-theme">Auto-mapear cámaras a puertas</h2>
        </div>

        {phase === 'idle' && (
          <>
            <p className="text-sm text-theme-secondary mb-4">
              Asigna automáticamente cámaras a puertas usando similitud de nombres. Solo afecta puertas
              sin asignación manual previa. Puedes ajustar resultados manualmente después.
            </p>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm text-theme-secondary hover:text-theme transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRun}
                className="px-4 py-2 text-sm font-medium bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
              >
                Ejecutar auto-mapeo
              </button>
            </div>
          </>
        )}

        {phase === 'running' && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            <p className="text-sm text-theme-secondary">Mapeando cámaras a puertas…</p>
          </div>
        )}

        {phase === 'done' && result && (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="rounded-lg border border-border p-4 text-center">
                <p className="text-2xl font-bold text-theme">{result.total}</p>
                <p className="text-xs text-theme-secondary mt-1">Puertas evaluadas</p>
              </div>
              <div className="rounded-lg border border-border p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{result.matched}</p>
                <p className="text-xs text-theme-secondary mt-1">Asignaciones nuevas</p>
              </div>
              <div className="rounded-lg border border-border p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                <p className="text-xs text-theme-secondary mt-1">Omitidas (ya tenían)</p>
              </div>
            </div>

            <div className="border border-border rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-surface-secondary">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-medium text-theme-secondary">Puerta</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-theme-secondary">Cámara asignada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {result.details.map((d) => (
                    <tr key={d.doorId}>
                      <td className="px-3 py-2 text-theme">{d.doorName}</td>
                      <td className="px-3 py-2">
                        {d.cameraName ? (
                          <span className="text-theme">{d.cameraName}</span>
                        ) : (
                          <span className="text-theme-secondary italic">Sin match</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
              >
                Cerrar y refrescar
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
